import { css } from '@emotion/react';
import { extendTheme } from '@mui/joy';
import { createTheme as materialExtendTheme } from '@mui/material';
// eslint-disable-next-line
import type {} from '@mui/x-date-pickers/themeAugmentation';

import { FloatingTextarea } from '@/components/form-elements/FloatingTextarea';

export const materialTheme = materialExtendTheme({
  palette: {
    primary: {
      50: '#8cd7fa',
      100: '#69b6e4',
      200: '#4694cd',
      300: '#2372b7',
      400: '#1261ac',
      500: '#0050A0',
      600: '#0a4691',
      700: '#143c82',
      800: '#1e3273',
      900: '#282864',
    },
    error: {
      main: '#e6007d',
    },
  },
  components: {
    MuiPickersTextField: {
      styleOverrides: {
        root: {
          '& .MuiPickersInputBase-root': {
            height: '3rem',
            backgroundColor: 'white',
            borderRadius: '6px',
          },
          '& .MuiPickersOutlinedInput-notchedOutline': {
            borderRadius: '6px',
            borderWidth: '1px',
            borderColor: '#e5e5e5',
            boxShadow:
              'var(--joy-shadowRing, 0 0 #000),0px 1px 2px 0px rgba(var(--joy-shadowChannel, 21 21 21) / var(--joy-shadowOpacity, 0.08))',
          },
          '&.Mui-error .MuiPickersOutlinedInput-notchedOutline, & .MuiPickersInputBase-root.Mui-error .MuiPickersOutlinedInput-notchedOutline':
            {
              borderColor: '#e6007d',
            },
        },
      },
    },
  },
});

export const theme = extendTheme({
  colorSchemes: {
    light: {
      palette: {
        primary: {
          50: '#8cd7fa',
          100: '#69b6e4',
          200: '#4694cd',
          300: '#2372b7',
          400: '#1261ac',
          500: '#0050A0',
          600: '#0a4691',
          700: '#143c82',
          800: '#1e3273',
          900: '#282864',

          outlinedBorder: 'var(--joy-palette-primary-500)',
          outlinedHoverBg: 'transparent',
          outlinedHoverBorder: 'var(--joy-palette-primary-900)',
          outlinedActiveBg: 'var(--joy-palette-primary-50)',
          outlinedActiveBorder: 'var(--joy-palette-primary-500)',

          softColor: 'var(--joy-palette-primary-500)',
          softBg: 'var(--joy-palette-primary-50)',
          softHoverBg: 'var(--joy-palette-primary-100)',

          solidHoverBg: 'var(--joy-palette-primary-900)',
          solidDisabledBg: 'var(--joy-palette-primary-500)',
          solidDisabledColor: 'var(--joy-palette-primary-200)',
        },

        neutral: {
          '50': '#fbfbfb',
          '100': '#f4f4f4',
          '200': '#ededed',
          '300': '#e5e5e5',
          '400': '#e0e0e0',
          '500': '#dadada',
          '600': '#d6d6d6',
          '700': '#d0d0d0',
          '800': '#cbcbcb',
          '900': '#c2c2c2',

          outlinedColor: 'var(--joy-palette-text-primary)',
          plainColor: 'var(--joy-palette-text-primary)',
          plainHoverColor: 'var(--joy-palette-text-primary)',
          softColor: 'var(--joy-palette-text-primary)',
          solidColor: 'var(--joy-palette-text-primary)',
        },

        danger: {
          '50': '#fce0ef',
          '100': '#f8b3d8',
          '200': '#f380be',
          '300': '#ee4da4',
          '400': '#ea2691',
          '500': '#e6007d',
          '600': '#e30075',
          '700': '#df006a',
          '800': '#db0060',
          '900': '#d5004d',
        },

        warning: {
          '50': '#fff6e0',
          '100': '#ffe9b3',
          '200': '#ffda80',
          '300': '#ffcb4d',
          '400': '#ffc026',
          '500': '#ffb500',
          '600': '#ffae00',
          '700': '#ffa500',
          '800': '#ff9d00',
          '900': '#ff8d00',
        },

        success: {
          '50': '#edf1e4',
          '100': '#d1ddbc',
          '200': '#b2c78f',
          '300': '#93b062',
          '400': '#7c9f40',
          '500': '#658e1e',
          '600': '#5d861a',
          '700': '#537b16',
          '800': '#497112',
          '900': '#375f0a',
        },

        text: {
          primary: 'var(--joy-palette-common-black, #000)',
          secondary: 'var(--joy-palette-common-black, #000)',
          tertiary: '#6d6d6d',
        },

        background: {
          surface: 'var(--joy-palette-common-white, #fff)',
          backdrop: 'rgba(218, 218, 218, 0.65)',
        },
      },
    },
  },

  fontFamily: {
    body: '"bwegt Text", sans-serif',
    display: '"bwegt Display", sans-serif',
  },

  components: {
    JoyAlert: {
      styleOverrides: {
        root: ({ ownerState, theme: overrideTheme }) => css`
          ${
            ownerState.variant === 'outlined' &&
            css`
              --variant-borderWidth: 2px;

              ${
                ownerState.color &&
                css`
                  background-color: ${overrideTheme.vars.palette[ownerState.color]['100']};
                `
              }

              ${
                ownerState.color === 'primary' &&
                css`
                  --variant-outlinedBorder: ${overrideTheme.vars.palette.primary['300']};
                `
              }
            `
          }
        `,
      },
    },

    JoyAutocomplete: {
      defaultProps: {
        size: 'lg',
        variant: 'plain',
      },
      styleOverrides: {
        root: ({ ownerState, theme: overrideTheme }) => css`
          ${
            ownerState.size === 'lg' &&
            css`
              font-size: ${overrideTheme.fontSize.md};
            `
          }

          ${
            ownerState.variant === 'outlined' &&
            css`
              box-shadow: none;
              --variant-borderWidth: 2px;
              --joy-focus-thickness: 2px;
            `
          }
        `,
      },
    },
    JoyButton: {
      defaultProps: {
        size: 'lg',
      },
      styleOverrides: {
        root: ({ ownerState }) => css`
          ${
            ownerState.variant === 'outlined' &&
            css`
              --variant-borderWidth: 2px;
            `
          }

          ${
            ownerState.size === 'lg' &&
            css`
              --Button-minHeight: 3rem;
            `
          }
        `,
      },
    },
    JoyChip: {
      styleOverrides: {
        root: ({ ownerState }) => css`
          ${
            ownerState.size === 'lg' &&
            css`
              --Chip-minHeight: 2.5rem;
            `
          }
        `,

        action: ({ theme: overrideTheme, ownerState }) => css`
          ${
            ownerState.variant === 'outlined' &&
            css`
              --variant-borderWidth: 2px;
              --variant-outlinedBorder: ${overrideTheme.vars.palette.primary['500']};
            `
          }
        `,
      },
    },
    JoyFormControl: {
      defaultProps: {
        size: 'lg',
      },
    },
    JoyFormLabel: {
      styleOverrides: {
        root: css`
          font-weight: bold;
        `,
      },
    },
    JoyInput: {
      defaultProps: {
        size: 'lg',
        variant: 'plain',
      },
      styleOverrides: {
        root: ({ ownerState, theme: overrideTheme }) => css`
          ${
            ownerState.size === 'lg' &&
            css`
              --Input-minHeight: 3rem;
              font-size: ${overrideTheme.fontSize.md};
            `
          }

          ${
            ownerState.readOnly &&
            css`
              --variant-borderWidth: 2px;
              border: var(--variant-borderWidth) solid #ffffff;

              ${
                ownerState.variant === 'plain' &&
                css`
                  background-color: transparent;
                `
              }

              ${
                ownerState.variant === 'outlined' &&
                css`
                  background-color: ${overrideTheme.vars.palette.neutral['100']};
                `
              }
            `
          }
        `,
      },
    },
    JoyTable: {
      styleOverrides: {
        root: ({ theme: overrideTheme }) => css`
          --TableCell-borderColor: ${overrideTheme.vars.palette.neutral['100']};
          --TableRow-hoverBackground: ${overrideTheme.vars.palette.common.white};

          thead th {
            white-space: normal;
            vertical-align: middle;
            background-color: ${overrideTheme.vars.palette.neutral['100']};
            border-bottom-width: 0;
          }

          tbody {
            tr {
              &:hover {
                cursor: pointer;
              }

              td,
              th {
                transition: border-bottom-color 100ms ease;
              }

              td,
              &:not(:last-of-type) > td,
              &:not(:last-of-type) > th[scope='row'] {
                border-bottom-style: solid;
                border-bottom-width: 2px;
              }
            }
          }
        `,
      },
      defaultProps: {
        hoverRow: true,
        noWrap: true,
      },
    },
    JoyTextarea: {
      defaultProps: {
        size: 'lg',
        variant: 'plain',
        slots: {
          textarea: FloatingTextarea,
        },
      },
      styleOverrides: {
        root: ({ ownerState, theme: overrideTheme }) => css`
          padding-block: 0;

          ${
            ownerState.size === 'lg' &&
            css`
              --Textarea-minHeight: ${overrideTheme.spacing(12)};
              font-size: ${overrideTheme.fontSize.md};
            `
          }

          ${
            ownerState.variant === 'outlined' &&
            css`
              box-shadow: none;
              --variant-borderWidth: 2px;
              --joy-focus-thickness: 2px;
            `
          }
        `,
      },
    },
    JoyLink: {
      styleOverrides: {
        root: css`
          font-weight: bold;
        `,
      },
    },
    JoyModalDialog: {
      defaultProps: {
        variant: 'soft',
        color: 'neutral',
      },
      styleOverrides: {
        root: ({ theme: overrideTheme, ownerState }) => css`
          --Card-radius: ${overrideTheme.vars.radius.xl};
          row-gap: 1.5rem;

          ${
            ownerState.variant === 'soft' &&
            ownerState.color === 'neutral' &&
            css`
              background-color: rgba(248, 248, 248, 0.75);
              backdrop-filter: blur(8px);
              box-shadow:
                -1px -1px 0 0 rgba(255, 255, 255, 0.8),
                1px 1px 0 0 rgba(109, 109, 109, 0.1),
                0 0 8px 0 rgba(109, 109, 109, 0.15);
            `
          }
        `,
      },
    },
    JoySelect: {
      defaultProps: {
        size: 'lg',
        variant: 'plain',
      },
    },
    JoySheet: {
      defaultProps: {
        variant: 'soft',
        color: 'neutral',
      },
      styleOverrides: {
        root: ({ theme: overrideTheme, ownerState }) => css`
          ${
            ownerState.variant === 'outlined' &&
            css`
              --variant-borderWidth: 2px;
            `
          }

          ${
            ownerState.color === 'neutral' &&
            ownerState.variant === 'outlined' &&
            css`
              background-color: ${overrideTheme.vars.palette.common.white};
            `
          }

          ${
            ownerState.color === 'neutral' &&
            ownerState.variant === 'soft' &&
            css`
              background-color: rgba(248, 248, 248, 0.75);
              backdrop-filter: blur(8px);
              box-shadow:
                -1px -1px 0 0 rgba(255, 255, 255, 0.8),
                1px 1px 0 0 rgba(109, 109, 109, 0.1),
                0 0 8px 0 rgba(109, 109, 109, 0.15);
            `
          }
        `,
      },
    },
    JoySnackbar: {
      defaultProps: {
        variant: 'soft',
        color: 'neutral',
      },
      styleOverrides: {
        root: ({ ownerState }) => css`
          ${
            ownerState.color === 'neutral' &&
            ownerState.variant === 'soft' &&
            css`
              background-color: rgba(248, 248, 248, 0.75);
              backdrop-filter: blur(8px);
              box-shadow:
                -1px -1px 0 0 rgba(255, 255, 255, 0.8),
                1px 1px 0 0 rgba(109, 109, 109, 0.1),
                0 0 8px 0 rgba(109, 109, 109, 0.15);
            `
          }
        `,
      },
    },
  },
});
